import React, { useState, useEffect, createContext, useContext, useRef } from "react"
import { User } from "@supabase/supabase-js"
import { supabase } from "@/integrations/supabase/client"
import {
  medianOneSignalLogin,
  medianOneSignalLogout,
  medianOneSignalInfo,
  medianOneSignalRegister,
  extractOneSignalSubscriptionId,
  isOneSignalSubscriptionActive,
  isMedianApp,
} from "@/lib/medianOneSignal"



interface AuthContextType {
  user: User | null
  loading: boolean
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  // Tracks the email already registered with OneSignal so repeated auth events
  // (token refresh, tab focus) don't re-register the same user over and over.
  const registeredEmailRef = useRef<string | null>(null)

  // Registers/unregisters the user with OneSignal via the Median bridge.
  // No-op outside the Median native app. Awaits the bridge, which on a cold
  // start is injected slightly after our JS runs.
  const syncOneSignal = async (userId: string | null, email: string | null) => {
    if (email) {
      if (registeredEmailRef.current === email) return
      registeredEmailRef.current = email

      // iOS permission can remain enabled while OneSignal's own subscription
      // becomes inactive (for example after reinstalling or updating a
      // TestFlight build). Renew the native subscription before linking the
      // account so an enabled phone is not left unreachable.
      const registered = await medianOneSignalRegister()
      const linked = await medianOneSignalLogin(email)
      if (!linked) {
        // Allow a later attempt (e.g. when the app returns to the foreground).
        registeredEmailRef.current = null
      }

      // Record proof of the link so the server has an exact second way to
      // reach this device, and so we can verify linking ever happened.
      try {
        const info = await medianOneSignalInfo(5000, true)
        const subscriptionId = extractOneSignalSubscriptionId(info)
        const active = isOneSignalSubscriptionActive(info)
        console.log("[Median/OneSignal] device sync", { email, registered, linked, subscriptionId, active })
        if (userId) {
          // Keep the account identifier even when an older bridge cannot read
          // it back. Only store a handset id we know is opted in.
          const payload = {
            onesignal_external_user_id: email,
            ...(subscriptionId && active ? { onesignal_subscription_id: subscriptionId } : {}),
          }
          const { error } = await supabase.from("profiles").update(payload).eq("id", userId)
          if (error) console.error("[Median/OneSignal] could not save ids on profile", error)

          // One row per handset, so signing in on a second phone does not
          // replace the first one.
          if (subscriptionId && active) {
            const { error: deviceError } = await supabase
              .from("onesignal_devices")
              .upsert(
                {
                  user_id: userId,
                  subscription_id: subscriptionId,
                  external_user_id: email,
                  platform: /iPhone|iPad|iPod/i.test(navigator.userAgent)
                    ? "ios"
                    : /Android/i.test(navigator.userAgent)
                    ? "android"
                    : null,
                  last_seen_at: new Date().toISOString(),
                },
                { onConflict: "subscription_id" },
              )
            if (deviceError) console.error("[Median/OneSignal] could not save device row", deviceError)
            if (typeof window !== "undefined") {
              window.dispatchEvent(
                new CustomEvent("median-onesignal-linked", { detail: { subscriptionId } }),
              )
            }
          }
        }

      } catch (error) {
        console.error("[Median/OneSignal] post-login info/save failed", error)
      }

    } else {
      if (registeredEmailRef.current === null) return
      registeredEmailRef.current = null
      medianOneSignalLogout()
    }
  }

  useEffect(() => {
    let mounted = true

    // Listen for auth changes FIRST so we never miss an event.
    // Important: only a real sign-out (or account deletion) clears the user.
    // A transient token-refresh hiccup on mobile (app resumed from background,
    // flaky network, two PWA instances refreshing at once) must NOT log the
    // user out — otherwise every app reload after a deploy looks like a logout.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return
        if (event === "SIGNED_OUT") {
          setUser(null)
          void syncOneSignal(null, null)
        } else if (session?.user) {
          setUser(session.user)
          void syncOneSignal(session.user.id, session.user.email ?? null)
        }
        setLoading(false)
      }
    )



    // Restore the persisted session, retrying once if the network was not
    // ready yet (common right after a cold start on mobile).
    const restoreSession = async () => {
      for (let attempt = 0; attempt < 2; attempt++) {
        const { data, error } = await supabase.auth.getSession()
        if (!mounted) return
        if (data?.session?.user) {
          setUser(data.session.user)
          void syncOneSignal(data.session.user.id, data.session.user.email ?? null)
          setLoading(false)
          return
        }

        if (!error) break
        await new Promise((r) => setTimeout(r, 800))
      }
      if (mounted) setLoading(false)
    }

    restoreSession()

    // Only auto-refresh tokens while the app is actually visible. Refreshing in
    // background tabs/instances can invalidate the refresh token for the
    // foreground one, which shows up as a random logout.
    const syncAutoRefresh = () => {
      if (document.visibilityState === "visible") {
        supabase.auth.startAutoRefresh()
      } else {
        supabase.auth.stopAutoRefresh()
      }
    }
    syncAutoRefresh()
    document.addEventListener("visibilitychange", syncAutoRefresh)

    // Inside the native app, retry the OneSignal link whenever the app comes
    // back to the foreground — the bridge may not have been ready on launch.
    const relinkOnForeground = async () => {
      if (document.visibilityState !== "visible") return
      if (!isMedianApp()) return
      if (registeredEmailRef.current) return
      const { data } = await supabase.auth.getSession()
      const sessionUser = data?.session?.user
      if (!mounted || !sessionUser?.email) return
      void syncOneSignal(sessionUser.id, sessionUser.email)
    }
    document.addEventListener("visibilitychange", relinkOnForeground)

    return () => {
      mounted = false
      document.removeEventListener("visibilitychange", syncAutoRefresh)
      document.removeEventListener("visibilitychange", relinkOnForeground)
      subscription.unsubscribe()
    }
  }, [])


  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
    </AuthContext.Provider>
  )
}